<script lang="ts" setup>
import { ref, watch, onMounted } from "vue";

import { ElMessage } from "element-plus";
import { useI18n } from "vue-i18n";
import { useMainStore } from "../store/main";
import { usePGRESTAPIStore } from "../store/pgrest_api";
import RegisterOfBond from "../classes/DB_Entities/RegisterOfBond";
import Decision from "../classes/Decision";
import EntityField from "./EntityField.vue";
import { useGenericMethodsVariables } from "../composables/genericMethodsVariables";
import PGRESTResult from "../classes/PGRESTResult";
import UserType from "../enums/UserType";

const emits = defineEmits(["save", "close", "fail-save"]);

const mainStore = useMainStore();
const pgrestapi = usePGRESTAPIStore();
const { t } = useI18n({ useScope: "global" });

const props = defineProps<{
  rob_id: number;
  newEntity?: boolean;
  view_only: boolean;
}>();

let loanAgreementsList = ref<any>([]);
const representativesList = ref<any[]>([]);

const {
  updateDecision,
  cleanObject,
  isLoading,
  fullObj,
  table_name,
  data,
  loadData,
  save,
  close,
  TransformFullObj,
  showError,
  showWarning
} = useGenericMethodsVariables();

watch(
  () => props.rob_id,
  async (newVal, oldVal) => {
    if (props.rob_id === undefined || props.rob_id == null || Number.isNaN(props.rob_id)) return;

    await loadRepresentatives()
    
    resetObjects();

    if (props.newEntity) return;
    
    await loadData(props.rob_id);
  }
);

const loadLoanAgreements = async () => {
  let result: PGRESTResult = new PGRESTResult();

  let url = `view_mosloanagreement_view1?select=id,ar_sumbashs&order=ar_sumbashs&approve_status=eq.1&omologiako=eq.true`;

  if (mainStore.loggedUser.user_type === UserType.Agent) {
    url += `&allowed_users=cs.{${mainStore.loggedUser.id}}`;
  } else if (mainStore.loggedUser.user_type === UserType.Supervisor) {
    url += `&portfolio_allowed_users=cs.{${mainStore.loggedUser.id}}`;
  }
  console.log(url);
  result = await pgrestapi.get(url);

  loanAgreementsList.value = [];
  for (let i = 0; i < result.data.length; ++i) {
    loanAgreementsList.value.push({
      value: result.data[i].id,
      label: result.data[i].ar_sumbashs,
    });
  }
};

const loadRepresentatives = async () => {
  let result: PGRESTResult = new PGRESTResult();
  representativesList.value = [];

  result = await pgrestapi.get("config_agent");

  if (result.error) {
    showError('Error retrieving representatives: ' + result.error.message)
    return;
  }

  for (let i = 0; i < result.data.length; i++) {
    representativesList.value.push({
      value: result.data[i].agent,
      label: result.data[i].agent,
    });
  }
};

const resetObjects = () => {
  data.value = new RegisterOfBond();
  fullObj.value = new RegisterOfBond();
  data.value.id = props.rob_id;
  fullObj.value.id = props.rob_id;
  TransformFullObj();
};

onMounted(async () => {
  table_name.value = RegisterOfBond.getTableNameStatic();
  cleanObject.value = new RegisterOfBond();

  console.log("rob_id", props.rob_id, "newEntity", props.newEntity);

  await loadRepresentatives()
  await loadLoanAgreements();

  if (props.newEntity === true) {
    resetObjects();
    return;
  }

  isLoading.value = true;
  await loadData(props.rob_id);
  fixPendingSelects();
  isLoading.value = false;
});

const onDecision = async (decision: Decision) => {
  isLoading.value = true;

  await updateDecision(decision);

  await loadData(props.rob_id);

  isLoading.value = false;
};

const onPreSave = async () => {
  if (props.newEntity as boolean) {
    if (fullObj.value.payment_representative.originalValue === "") {
      showWarning(t("errors.missing_payment_representative"))
      return;
    }
  }

  await save();
};

const fixPendingSelects = async () => {
  for (let field of Object.keys(fullObj.value)) {
    if (fullObj.value[field].pendingValue === undefined) continue; // Skip field if no pending value

    // Payment Representative 
    if (field === "payment_representative") {
      fullObj.value[field].pendingValue[
        "pendingDisplayValue"
      ] = fullObj.value[field].pendingValue.value;

      fullObj.value[field].pendingValue[
        "originalDisplayValue"
      ] = fullObj.value[field].originalValue;
    }
  }
};
</script>

<template>
  <div v-loading="isLoading">
    <el-form>
      <EntityField
        type="select"
        label="Loan Agreement"
        :object="fullObj.loan_agreement_id"
        :options="loanAgreementsList"
        @decision="onDecision"
        :is-disabled="props.view_only"
      />

      <EntityField
        type="select"
        :object="fullObj.payment_representative"
        label="Payment Representative"
        :options="representativesList"
        :is-disabled="props.view_only"
        @decision="onDecision"
      />

      <EntityField
        type="input"
        label="Replacement Mechanism"
        :object="fullObj.replacement_mechanism"
        @decision="onDecision" :is-disabled="props.view_only"
      />

      <el-row :gutter="10">
        <el-col :span="24">
          <el-form-item class="btn-wrapper-components">
            <el-button v-if="!props.view_only" type="primary" @click="onPreSave">{{$t('generic.save')}}</el-button>
            <el-button @click="close">{{$t('generic.close')}}</el-button>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
  </div>
</template>

<style scoped></style>
